/**
 * Trial expiration banner — fetches billing/trial status once after connection,
 * caches it, and provides info for rendering an upgrade banner when the trial
 * has <= 2 days remaining.
 */

interface TrialInfo {
  active: boolean;
  days_remaining: number;
  expires_at: string;
}

interface TrialBannerState {
  fetched: boolean;
  trial: TrialInfo | null;
  dismissed: boolean;
  hasSubscription: boolean;
}

const state: TrialBannerState = {
  fetched: false,
  trial: null,
  dismissed: false,
  hasSubscription: false,
};

const DISMISS_KEY = "alita.trial-banner-dismissed";

/**
 * Fetch billing status and cache trial info. Call once after connection.
 * Fire-and-forget — does not throw.
 */
export async function fetchTrialStatus(): Promise<void> {
  if (state.fetched) return;
  try {
    const { apiFetchWithJWT } = await import("./clerk-auth.ts");
    const res = await apiFetchWithJWT("/api/billing/status");
    if (!res.ok) return;
    const data = await res.json();
    state.hasSubscription = data.has_subscription === true;
    state.trial = data.trial ?? null;
    state.fetched = true;

    // Check if previously dismissed for this trial period
    try {
      const dismissedAt = sessionStorage.getItem(DISMISS_KEY);
      if (dismissedAt) state.dismissed = true;
    } catch { /* no sessionStorage */ }
  } catch {
    // best effort
  }
}

/**
 * Returns banner info if the banner should be shown:
 * - Trial is active
 * - days_remaining <= 2
 * - User has NO paid subscription
 * - Not dismissed this session
 */
export function getTrialBannerInfo(): { daysRemaining: number; expiresAt: string } | null {
  if (!state.fetched || state.dismissed) return null;
  if (state.hasSubscription) return null;
  if (!state.trial || !state.trial.active) return null;
  if (state.trial.days_remaining > 2) return null;
  return {
    daysRemaining: state.trial.days_remaining,
    expiresAt: state.trial.expires_at,
  };
}

/** Dismiss the banner for this session. */
export function dismissTrialBanner(): void {
  state.dismissed = true;
  try {
    sessionStorage.setItem(DISMISS_KEY, new Date().toISOString());
  } catch { /* no sessionStorage */ }
}
